import {
  afterPasswordLogin,
  biometricAvailable,
  biometricDeclined,
  biometricEnabled,
  declineBiometric,
  enableBiometric,
  isNativeApp,
} from '@matix/ui';
import type { AuthData } from '../../lib/cashierAuthClient';

/**
 * Called once a sign-in has reached `stage: 'session'`.
 *
 * Two jobs, both silent in a browser:
 *   1. Remember the workspace so the next sign-in screen knows it.
 *   2. The FIRST time, offer to unlock with the fingerprint / face. A refusal
 *      is remembered, so the cashier is asked once — not after every sale.
 *
 * Never throws. A keystore that refuses must not disturb a sign-in that has
 * already succeeded, so every failure here ends as "no biometrics", never as
 * an error the operator has to read.
 */
export async function offerBiometricAfterLogin(data: AuthData): Promise<void> {
  if (!isNativeApp()) return;
  const json = JSON.stringify(data);
  const slug = data.stage === 'session' ? data.user.tenant_slug : '';

  try {
    await afterPasswordLogin(json, slug);

    if (biometricEnabled() || biometricDeclined()) return;
    if (!(await biometricAvailable())) return;

    const ar = document.documentElement.lang !== 'en';
    const ask = ar
      ? 'تحب تدخل بالبصمة أو بصمة الوجه المرة الجاية؟'
      : 'Use your fingerprint or face to sign in next time?';
    if (window.confirm(ask)) await enableBiometric(json);
    else declineBiometric();
  } catch {
    /* biometrics unavailable — the till keeps asking for the password */
  }
}
